import { useState } from 'react';
import { useQuery } from 'react-query';

import { EyeIcon } from '../../../../assets/svg';
import { TicketIcon } from '../../../../assets/svg/TicketIcon';
import { DateAndTime } from '../../../../components/date-and-time';
import { CONSTANTS } from '../../../../config/constants';
import { useMerchant } from '../../../../hooks/useMerchant';
import { Payment } from '../../../../models/common-payments';
import { ColumnInterface, Filters } from '../../../../models/table';
import { PaymentLinksCustomServices } from '../../../../services/payment-links-custom';
import { defaultPagination } from '../../../../utils/defaults';
import { ContainerActions, SimpleButton, SkeletonSVG } from '../../pages/links/styles';
import { CustomPaymentLinksModal } from '../custom-modal';
import { ContainerActionsLinksComponent } from '../links-table/ContainerActionsComponent';
import { Detail, Item } from '../modal-payment/components/detail';
import { StatusCustomPaymentLink } from '../status';
import { PaymentLinkTable } from '../table';
import { AmountContainer, NoWrapContainer } from '../table/styles';
import { Container, HeaderDetails, TableContainer } from './styles';
import { ModalPaymentProps } from './types';

const defaultFilters: Filters = {
	orderBy: 'asc',
	key: '',
	status: undefined,
	dateRange: undefined,
};

const ModalLink = ({
	show,
	onClose,
	data,
	positionType,
	handleShowModalPayment,
	allowClickOutside,
}: ModalPaymentProps) => {
	const [filters, setFilters] = useState(defaultFilters);
	const [pagination, setPagination] = useState(defaultPagination);
	const [total, setTotal] = useState(0);

	const { merchantSelected, getFlowBankorStp } = useMerchant();
	const flowBanksOrStp = getFlowBankorStp();

	const LinkUsed: Item[] = [
		{
			title: 'ID enlace',
			value: data.name,
		},
		{
			title: 'Pagos máx.',
			value:
				data.numsuccesspayments_remaining !== null && data.numsuccesspayments_remaining !== 0
					? String(data.numsuccesspayments_remaining)
					: 'Sin límite',
		},
	];

	const OpeningsPayments: Item[] = [
		{ title: 'Aperturas totales', value: String(data.openings) },
		{ title: 'Pagos exitosos', value: String(data.numsuccesspayments) },
	];

	const InitDate: Item[] = [
		{ title: 'Fecha de inicio', value: data.createdatdate },
		{ title: '', value: data.createdattime },
	];

	const EndDate: Item[] = [
		{
			title: 'Fecha de término',
			value: data.endexpiredatdate !== 'null' ? data.endexpiredatdate : 'Sin término',
		},
		{
			title: '',
			value: data.endexpiredattime !== 'null' ? data.endexpiredattime : ' ',
		},
	];

	const {
		data: table,
		isFetching,
		isLoading: isLoadingTable,
	} = useQuery({
		useErrorBoundary: true,
		keepPreviousData: true,
		queryKey: [
			CONSTANTS.services.paymentLinksCustom.getLinkPayments,
			merchantSelected?.code,
			pagination,
			filters,
		],
		queryFn: () =>
			PaymentLinksCustomServices.getAllPayments({
				merchantCode: merchantSelected?.code as string,
				pagination,
				filters: {
					...filters,
					stateSelected: filters?.status,
					dateRange: {
						from: filters?.dateRange?.from,
						to: filters?.dateRange?.to,
					},
					preSessionTokenSelected: data.token,
				},
			}),
		onSuccess: (data) => {
			if (data) setTotal(data.total);
		},
	});

	const handleDownloadVoucher = (rowData: Payment) => {
		if (rowData.urlCEP) window.open(rowData.urlCEP, '_blank');
		else if (rowData.validationMailPdf) {
			window.open(rowData.validationMailPdf, '_blank');
		}
	};

	const getColumns = () => {
		const columns: ColumnInterface<Payment>[] = [
			{
				sortField: true,
				title: 'Fecha de creación',
				propertyName: 'createdatDate',
				align: 'left',
				cellRender: ({ createdatDate, createdatTime }) => (
					<DateAndTime date={createdatDate} time={createdatTime} />
				),
			},
			{
				sortField: true,
				title: 'Monto',
				align: 'left',
				propertyName: 'amount',
				cellRender: ({ amount }) => <AmountContainer>{amount}</AmountContainer>,
			},
			{
				title: 'RUT Pagador',
				align: 'left',
				propertyName: 'rut',
				cellRender: ({ rut }) => <NoWrapContainer>{rut}</NoWrapContainer>,
			},
			{
				sortField: true,
				title: 'Banco',
				align: 'left',
				propertyName: 'bankName',
				cellRender: ({ bankName }) => (
					<>{bankName === 'DESCONOCIDO' && merchantSelected?.country === 'MX' ? null : bankName}</>
				),
			},
			{
				sortField: true,
				title: 'Correo electrónico',
				align: 'left',
				propertyName: 'email',
				cellRender: ({ email }) => <>{email}</>,
			},
			{
				title: 'Estatus',
				align: 'left',
				propertyName: 'status',
				cellRender: ({ status }) => (
					<AmountContainer>
						<StatusCustomPaymentLink status={status} />
					</AmountContainer>
				),
			},
			{
				title: '',
				propertyName: '',
				align: 'left',
				cellRender: (rowData: Payment) => (
					<ContainerActionsLinksComponent disabled={false}>
						<ContainerActions>
							<SimpleButton
								onClick={() => handleDownloadVoucher(rowData)}
								disabled={!rowData.urlCEP && !rowData.validationMailPdf}
							>
								{flowBanksOrStp && (rowData.urlCEP || rowData.validationMailPdf) ? (
									<TicketIcon />
								) : (
									<SkeletonSVG />
								)}
							</SimpleButton>
							<SimpleButton onClick={() => handleShowModalPayment(rowData)}>
								<EyeIcon />
							</SimpleButton>
						</ContainerActions>
					</ContainerActionsLinksComponent>
				),
			},
		];

		return columns;
	};

	return (
		<CustomPaymentLinksModal
			show={show}
			onClose={onClose}
			title={data.name}
			subTitle="Enlace de cobro"
			type="xl-large"
			allowCloseModal
			positionType={positionType || 'top-right'}
			allowClickOutside={allowClickOutside}
		>
			<Container>
				<HeaderDetails>
					<Detail items={LinkUsed} />
					<Detail items={OpeningsPayments} />
					<Detail items={InitDate} />
					<Detail items={EndDate} />
				</HeaderDetails>
				<TableContainer>
					<PaymentLinkTable
						titleTable=" "
						allowExport
						data={table?.tableInfo || []}
						columns={getColumns()}
						filters={filters}
						hasFilters
						hasDateFilters={true}
						hasSearch
						isLoading={isFetching || isLoadingTable}
						onFilterChange={setFilters}
						pagination={pagination}
						setPagination={setPagination}
						stateList={table?.typeOfStatus || []}
						totalResults={total}
					/>
				</TableContainer>
			</Container>
		</CustomPaymentLinksModal>
	);
};

export { ModalLink };
